import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  getAvailabilitySettings,
  getBlockedDates,
  getAvailableTimeSlots,
  isDateFullyBlocked,
  fetchBookedSlotsForDate,
} from '../services/availabilityService';
import { createHoldRequest } from '../services/holdService';
import { formatDateForApi, getBookingWeekday } from '../utils/bookingDates';
import { pagePadding } from '../styles/layout';
import {
  GOLD,
  GOLD_RGBA,
  PANEL_BG,
  BOOKING_BG,
  INPUT_BG,
} from '../constants/booking';
import { generateBookingHours, isTimeRangeAvailable } from '../components/booking/bookingHelpers';
import BookingCalendar from '../components/booking/BookingCalendar';
import TimeSlotPicker from '../components/booking/TimeSlotPicker';
import DurationPicker from '../components/booking/DurationPicker';

const columnPanelStyle = {
  backgroundColor: PANEL_BG,
  borderRadius: '1rem',
  padding: '2rem',
  border: `1px solid ${GOLD_RGBA}, 0.25)`,
  display: 'flex',
  flexDirection: 'column',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: INPUT_BG,
  color: '#F5F0E8',
  border: '1px solid #4b5563',
  borderRadius: '0.75rem',
  padding: '0.875rem 1rem',
  fontSize: '1rem',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: '600',
  color: '#F5F0E8',
  marginBottom: '0.5rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
};

const HOLD_DURATION_OPTIONS = [2, 3, 4, 5, 6, 7, 8];
const MIN_HOLD_HOURS = 2;

const RequestDate = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [durationHours, setDurationHours] = useState(MIN_HOLD_HOURS);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [loadingBookedTimes, setLoadingBookedTimes] = useState(false);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tentativeBookingHours, setTentativeBookingHours] = useState([]);
  const [availabilitySettings, setAvailabilitySettings] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hold, setHold] = useState(null);

  const durationOptions = HOLD_DURATION_OPTIONS;
  const canSubmit = !!(selectedDate && selectedTime && fullName.trim() && email.trim() && phoneNumber.trim());

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [settings, blocked] = await Promise.all([getAvailabilitySettings(), getBlockedDates()]);
        setAvailabilitySettings(settings);
        setBlockedDates(blocked);
      } catch (err) {
        console.error('Error loading availability settings:', err);
        setError('Failed to load availability. Please refresh the page.');
      }
    })();
  }, []);

  const isDayAvailable = useCallback(
    (date) => {
      if (!date || !availabilitySettings) return false;
      if (isDateFullyBlocked(date, blockedDates)) return false;
      const dayName = getBookingWeekday(date);
      const schedule = availabilitySettings.weeklySchedule[dayName];
      if (!schedule || !schedule.enabled) return false;
      return getAvailableTimeSlots(date, availabilitySettings.weeklySchedule, blockedDates, availabilitySettings.generalSettings).length > 0;
    },
    [availabilitySettings, blockedDates]
  );

  const getMaxAvailableHoursForTime = useCallback(
    (startTime) => {
      if (!startTime) return HOLD_DURATION_OPTIONS[HOLD_DURATION_OPTIONS.length - 1];
      let maxHours = MIN_HOLD_HOURS;
      for (let h = MIN_HOLD_HOURS; h <= HOLD_DURATION_OPTIONS[HOLD_DURATION_OPTIONS.length - 1]; h++) {
        if (isTimeRangeAvailable(startTime, h, bookedTimes)) maxHours = h;
        else break;
      }
      return maxHours;
    },
    [bookedTimes]
  );

  const fetchBookedTimes = async (date) => {
    if (!date) return;
    setLoadingBookedTimes(true);
    setError(null);
    try {
      const fullBookings = await fetchBookedSlotsForDate(date);
      const bookedList = [];
      fullBookings.forEach((data) => {
        bookedList.push(...generateBookingHours(data.time, data.totalHours));
      });
      setBookedTimes(bookedList);
      setExistingBookings(fullBookings);
    } catch (err) {
      console.error('Error fetching booked times:', err);
      setError('Failed to load available times. Please try again.');
    } finally {
      setLoadingBookedTimes(false);
    }
  };

  useEffect(() => {
    if (selectedDate) fetchBookedTimes(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate && availabilitySettings) {
      setAvailableTimeSlots(
        getAvailableTimeSlots(selectedDate, availabilitySettings.weeklySchedule, blockedDates, availabilitySettings.generalSettings)
      );
    } else {
      setAvailableTimeSlots([]);
    }
  }, [selectedDate, availabilitySettings, blockedDates]);

  useEffect(() => {
    if (!availabilitySettings || selectedDate) return;
    let checkDate = new Date();
    checkDate.setDate(checkDate.getDate() + 1);
    for (let i = 0; i < 60; i++) {
      if (isDayAvailable(checkDate)) {
        setSelectedDate(checkDate);
        setCurrentMonth(new Date(checkDate.getFullYear(), checkDate.getMonth(), 1));
        break;
      }
      checkDate = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate() + 1);
    }
  }, [availabilitySettings, blockedDates, selectedDate, isDayAvailable]);

  useEffect(() => {
    if (selectedTime && selectedDate) {
      setTentativeBookingHours(generateBookingHours(selectedTime, durationHours));
    } else {
      setTentativeBookingHours([]);
    }
  }, [selectedTime, durationHours, selectedDate]);

  const handleDateSelect = (date) => {
    if (!isDayAvailable(date)) return;
    setSelectedDate(date);
    setSelectedTime(null);
    setAvailableTimeSlots([]);
    fetchBookedTimes(date);
  };

  const handleDurationSelect = (hours) => {
    if (!selectedTime) {
      setDurationHours(hours);
      return;
    }
    if (!isTimeRangeAvailable(selectedTime, hours, bookedTimes)) {
      setError('That duration conflicts with an existing booking for this start time.');
      return;
    }
    setError(null);
    setDurationHours(hours);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;
    if (isDateFullyBlocked(selectedDate, blockedDates)) {
      setError('This date is blocked. Please choose another date.');
      return;
    }
    if (!availableTimeSlots.includes(selectedTime)) {
      setError('The selected time is not available. Please choose another time.');
      return;
    }
    if (!isTimeRangeAvailable(selectedTime, durationHours, bookedTimes)) {
      setError('Selected time range conflicts with existing bookings. Please choose another time.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const created = await createHoldRequest({
        selectedDate: formatDateForApi(selectedDate),
        selectedTime,
        totalHours: durationHours,
        website,
        customerInfo: {
          fullName,
          email,
          phoneNumber,
          notes,
        },
      });
      setHold(created);
    } catch (err) {
      setError(err.message || 'Unable to place a hold. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!availabilitySettings) {
    return (
      <div className="dd-page dd-booking" style={{ padding: pagePadding(isMobile), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: '#111111', borderRadius: '1rem', padding: '2rem', textAlign: 'center', border: `1px solid ${GOLD_RGBA}, 0.25)` }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#F5F0E8' }}>
            Loading availability...
          </h2>
        </div>
      </div>
    );
  }

  if (hold) {
    return (
      <div className="dd-page dd-booking" style={{ backgroundColor: BOOKING_BG, padding: pagePadding(isMobile) }}>
        <Helmet>
          <title>Hold Confirmed | Digital Divot Golf</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="dd-booking-content" style={{ maxWidth: '40rem', margin: '0 auto' }}>
          <div style={{ ...columnPanelStyle, textAlign: 'center' }}>
            <h1 style={{ color: GOLD, marginTop: 0 }}>Your date is on hold</h1>
            <p style={{ color: '#F5F0E8', lineHeight: 1.6 }}>
              This is not a paid booking. We reserved this window on our calendar and will follow up to confirm.
            </p>
            <p style={{ fontFamily: 'monospace', fontSize: '1.25rem', color: GOLD, fontWeight: 700, letterSpacing: '0.08em' }}>
              {hold.confirmationCode}
            </p>
            <p style={{ color: '#9ca3af' }}>
              {hold.date} at {hold.time} · {hold.totalHours} hours
            </p>
            <p style={{ color: '#9ca3af', fontSize: '0.9375rem' }}>
              We’ll email you a confirmation. Use your code on{' '}
              <Link to="/find-booking" style={{ color: GOLD }}>Find Booking</Link> to look it up.
            </p>
            <Link
              to="/booking"
              style={{
                display: 'inline-flex',
                marginTop: '1rem',
                backgroundColor: GOLD,
                color: '#000',
                textDecoration: 'none',
                fontWeight: 700,
                borderRadius: '9999px',
                padding: '0.75rem 1.5rem',
              }}
            >
              Convert to a paid booking
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dd-page dd-booking" style={{ backgroundColor: BOOKING_BG, padding: pagePadding(isMobile) }}>
      <Helmet>
        <title>Request a Date | Digital Divot Golf</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta
          name="description"
          content="Check Digital Divot availability and request a date. Places a hold on our calendar — not a paid booking."
        />
      </Helmet>
      <div className="dd-booking-content">
        <div className="dd-page-intro">
          <h1>Check Availability</h1>
          <p>Request a date to hold our time. This is not a booking and no payment is taken.</p>
        </div>

        <form onSubmit={handleSubmit} className="dd-booking-layout">
          <div className="dd-booking-panel" style={{ ...columnPanelStyle, height: 'auto' }}>
            <BookingCalendar
              currentMonth={currentMonth}
              selectedDate={selectedDate}
              isDayAvailable={isDayAvailable}
              onDateSelect={handleDateSelect}
              onPrevMonth={() => setCurrentMonth((prev) => { const d = new Date(prev); d.setMonth(prev.getMonth() - 1); return d; })}
              onNextMonth={() => setCurrentMonth((prev) => { const d = new Date(prev); d.setMonth(prev.getMonth() + 1); return d; })}
              isMobile={isMobile}
            />
            <TimeSlotPicker
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              availableTimeSlots={availableTimeSlots}
              bookedTimes={bookedTimes}
              durationHours={durationHours}
              tentativeBookingHours={tentativeBookingHours}
              existingBookings={existingBookings}
              availabilitySettings={availabilitySettings}
              loadingBookedTimes={loadingBookedTimes}
              onTimeSelect={setSelectedTime}
              isMobile={isMobile}
            />
            <DurationPicker
              durationOptions={durationOptions}
              durationHours={durationHours}
              minHoursForPackage={MIN_HOLD_HOURS}
              selectedPackage={null}
              selectedTime={selectedTime}
              tentativeBookingHours={tentativeBookingHours}
              bookedTimes={bookedTimes}
              getMaxAvailableHoursForTime={getMaxAvailableHoursForTime}
              onDurationSelect={handleDurationSelect}
              isMobile={isMobile}
              introText="Choose how long you need us on site. We’ll hold that window on the calendar."
            />
          </div>

          <div className="dd-booking-panel" style={{ ...columnPanelStyle, height: 'auto', position: 'relative' }}>
            <h3 style={{ color: '#F5F0E8', marginTop: 0 }}>Your details</h3>
            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={labelStyle}>Name</span>
              <input value={fullName} onChange={(e) => setFullName(e.target.value)} required autoComplete="name" style={inputStyle} />
            </label>
            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={labelStyle}>Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" style={inputStyle} />
            </label>
            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={labelStyle}>Phone</span>
              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required autoComplete="tel" style={inputStyle} />
            </label>
            <label style={{ display: 'block', marginBottom: '1rem' }}>
              <span style={labelStyle}>Notes (optional)</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                maxLength={500}
                style={{ ...inputStyle, resize: 'vertical', minHeight: '6rem' }}
                placeholder="Venue, guest count, or anything we should know."
              />
            </label>
            <div aria-hidden="true" style={{ display: 'none' }}>
              <label>
                Website
                <input value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" />
              </label>
            </div>

            {error && (
              <p style={{ color: '#f87171', margin: '0 0 1rem' }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={!canSubmit || submitting}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: '9999px',
                backgroundColor: GOLD,
                color: '#000',
                fontWeight: 700,
                padding: '0.9rem 1.25rem',
                cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed',
                opacity: canSubmit && !submitting ? 1 : 0.6,
              }}
            >
              {submitting ? 'Placing hold…' : 'Request this date'}
            </button>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.75rem', marginBottom: 0, lineHeight: 1.5 }}>
              We’ll email you a confirmation and follow up to lock in the details.
            </p>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem', lineHeight: 1.5 }}>
              Ready to pay a deposit instead?{' '}
              <Link to="/booking" style={{ color: GOLD }}>Book now</Link>.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestDate;
